import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_ADMIN_API_URL || '';

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  } catch (error) {
    console.error('Error getting auth headers:', error);
    throw new Error('Authentication required');
  }
}

async function apiRequest(endpoint, options = {}) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Dashboard stats
export async function getStats() {
  return apiRequest('/admin/stats');
}

// Users
export async function getUsers(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.set('limit', params.limit);
  if (params.nextToken) queryParams.set('nextToken', params.nextToken);
  if (params.search) queryParams.set('search', params.search);
  if (params.status) queryParams.set('status', params.status);
  
  const query = queryParams.toString();
  return apiRequest(`/admin/users${query ? `?${query}` : ''}`);
}

export async function getUser(userId) {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}`);
}

export async function createUsers(emails) {
  return apiRequest('/admin/users', {
    method: 'POST',
    body: JSON.stringify({ emails }),
  });
}

export async function deleteUser(userId) {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

export async function previewUserDeletion(userId) {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/preview-delete`);
}

// Step-by-step preview functions for progress tracking
export async function previewDeletionCognito(userId) {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/preview-delete/cognito`);
}

export async function previewDeletionS3(userId, identityId) {
  const params = identityId ? `?identityId=${encodeURIComponent(identityId)}` : '';
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/preview-delete/s3${params}`);
}

export async function previewDeletionCrewBookTable(userId, tableName) {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/preview-delete/crewbook/${tableName}`);
}

export async function previewDeletionShipBook(userId) {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/preview-delete/shipbook`);
}

export async function bulkDeleteUsers(userIds) {
  return apiRequest('/admin/users/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });
}

// Expired accounts
export async function getExpiredAccounts(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.set('limit', params.limit);
  if (params.nextToken) queryParams.set('nextToken', params.nextToken);
  
  const query = queryParams.toString();
  return apiRequest(`/admin/accounts/expired${query ? `?${query}` : ''}`);
}

export async function previewCleanup() {
  return apiRequest('/admin/accounts/cleanup/preview', {
    method: 'POST',
  });
}

export async function runCleanup(userIds) {
  return apiRequest('/admin/accounts/cleanup', {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });
}

// Subscriptions
export async function updateSubscription(userId, updates) {
  return apiRequest(`/admin/subscriptions/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export default {
  getStats,
  getUsers,
  getUser,
  createUsers,
  deleteUser,
  previewUserDeletion,
  bulkDeleteUsers,
  getExpiredAccounts,
  previewCleanup,
  runCleanup,
  updateSubscription,
};
